import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const LiveMarketTicker = () => {
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchMarketData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchMarketData, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMarketData = async () => {
    try {
      const response = await axios.get(`${API}/market/prices`);
      const data = response.data;

      const marketItems = [];

      // Bitcoin
      if (data.btc && data.btc.price > 0) {
        marketItems.push({
          symbol: 'BTC',
          name: 'Bitcoin',
          price: data.btc.price,
          change: data.btc.change_24h
        });
      }

      // Ethereum
      if (data.eth && data.eth.price > 0) {
        marketItems.push({
          symbol: 'ETH',
          name: 'Ethereum',
          price: data.eth.price,
          change: data.eth.change_24h
        });
      }

      // Gold (from XAUT)
      if (data.gold && data.gold.price > 0) {
        marketItems.push({
          symbol: 'GOLD',
          name: 'Gold/oz',
          price: data.gold.price,
          change: data.gold.change_24h
        });
      }

      // Total Market Cap
      if (data.total_market_cap && data.total_market_cap.value > 0) {
        marketItems.push({
          symbol: 'MCAP',
          name: 'Crypto Market',
          price: data.total_market_cap.value / 1e12, // In trillions
          change: data.total_market_cap.change_24h,
          suffix: 'T'
        });
      }

      // BTC Dominance
      if (data.btc_dominance > 0) {
        marketItems.push({
          symbol: 'BTC.D',
          name: 'BTC Dominance',
          price: data.btc_dominance,
          hideChange: true,
          suffix: '%'
        });
      }

      setMarketData(marketItems);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching market data:', err);
      // Keep existing data on error
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, suffix) => {
    if (suffix === 'T') {
      return `$${price.toFixed(2)}T`;
    }
    if (suffix === '%') {
      return `${price.toFixed(1)}%`;
    }
    if (price > 10000) {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    if (price > 100) {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${price.toFixed(2)}`;
  };

  const formatChange = (change) => {
    if (change === undefined || change === null) return '';
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  if (loading && marketData.length === 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 w-full bg-[#050505] border-b border-white/10 h-10 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#C5A059] rounded-full animate-pulse"></div>
          <span className="text-gray-500 text-xs">Loading market data...</span>
        </div>
      </div>
    );
  }

  if (marketData.length === 0) {
    return null; // Don't show ticker if no data
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 w-full bg-[#050505] border-b border-white/10 overflow-hidden" data-testid="market-ticker">
      <div className="flex items-center h-10">
        {/* Live indicator */}
        <div className="flex items-center gap-2 px-4 border-r border-white/10 flex-shrink-0">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-gray-400 text-xs font-medium uppercase tracking-wider">Live</span>
        </div>
        
        {/* Scrolling container */}
        <div className="flex-1 overflow-hidden">
          <div 
            className="flex items-center gap-6 whitespace-nowrap animate-scroll"
            style={{
              animation: 'scroll 35s linear infinite',
            }}
          >
            {/* Duplicate items for seamless scrolling */}
            {[...marketData, ...marketData, ...marketData].map((item, index) => (
              <div 
                key={`${item.symbol}-${index}`} 
                className="flex items-center gap-2 px-3"
              >
                <span className="text-[#C5A059] text-xs font-bold">{item.symbol}</span>
                <span className="text-white text-xs font-mono font-medium">
                  {formatPrice(item.price, item.suffix)}
                </span>
                {!item.hideChange && item.change !== undefined && (
                  <span 
                    className={`text-xs font-medium flex items-center gap-0.5 ${
                      item.change >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {item.change >= 0 ? (
                      <TrendingUp size={11} />
                    ) : (
                      <TrendingDown size={11} />
                    )}
                    {formatChange(item.change)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Source */}
        <div className="px-3 border-l border-white/10 flex-shrink-0">
          <span className="text-gray-600 text-xs">CoinPaprika</span>
        </div>
      </div>
      
      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.33%);
          }
        }
        .animate-scroll {
          animation: scroll 35s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default LiveMarketTicker;
